import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import { toastr } from 'react-redux-toastr';

import { loginWithGoogle, loginWithApple } from '../actions/customerActions';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;
const APPLE_SERVICE_ID =
    process.env.REACT_APP_APPLE_SERVICE_ID || 'com.videocallshop.signin';

const loadScript = (src, id) =>
    new Promise((resolve, reject) => {
        const existing = document.getElementById(id);
        if (existing) {
            if (existing.dataset.loaded === 'true') {
                return resolve();
            }
            existing.addEventListener('load', resolve);
            existing.addEventListener('error', reject);
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.id = id;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            script.dataset.loaded = 'true';
            resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
    });

// Login panel with "Continuar con Google" (Google Identity Services) and
// "Continuar con Apple" (Sign in with Apple JS, popup mode). Both flows end
// in a provider token POSTed to the API, which mints the customer session.
const CustomerLogin = ({
    title = 'Iniciá sesión para continuar',
    subtitle = 'Usá tu cuenta de Google o Apple. Es gratis y solo toma unos segundos.',
    isLoggingIn,
    loginWithGoogle,
    loginWithApple,
}) => {
    const googleButtonRef = useRef(null);
    const [googleReady, setGoogleReady] = useState(false);

    useEffect(() => {
        let cancelled = false;

        loadScript('https://accounts.google.com/gsi/client', 'google-gsi')
            .then(() => {
                if (cancelled || !window.google || !googleButtonRef.current) {
                    return;
                }

                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: (response) => loginWithGoogle(response.credential),
                });

                window.google.accounts.id.renderButton(
                    googleButtonRef.current,
                    {
                        theme: 'outline',
                        size: 'large',
                        text: 'continue_with',
                        width: 280,
                    }
                );

                setGoogleReady(true);
            })
            .catch(() => {
                if (!cancelled) {
                    toastr.error(
                        'Error',
                        'No se pudo cargar el login de Google.'
                    );
                }
            });

        return () => {
            cancelled = true;
        };
    }, [loginWithGoogle]);

    const handleAppleLogin = async () => {
        try {
            await loadScript(
                'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
                'apple-signin'
            );

            window.AppleID.auth.init({
                clientId: APPLE_SERVICE_ID,
                scope: 'name email',
                redirectURI: window.location.origin,
                usePopup: true,
            });

            const response = await window.AppleID.auth.signIn();

            // name/email only arrive on the FIRST Apple authorization
            const name = response.user && response.user.name;

            loginWithApple({
                identityToken: response.authorization.id_token,
                authorizationCode: response.authorization.code,
                fullName: name
                    ? {
                          givenName: name.firstName,
                          familyName: name.lastName,
                      }
                    : undefined,
            });
        } catch (err) {
            // closing the Apple popup rejects with { error: 'popup_closed_by_user' }
            if (err && err.error === 'popup_closed_by_user') {
                return;
            }
            toastr.error('Error', 'No se pudo iniciar sesión con Apple.');
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
            <Typography variant="h5" component="h2" align="center" gutterBottom>
                {title}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mb: 3 }}
            >
                {subtitle}
            </Typography>

            {(isLoggingIn || !googleReady) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}
            {/* The GIS container div must NEVER have React-rendered children:
                Google replaces its content and React's removeChild crashes.
                It also must stay mounted while logging in (the GIS button is
                only rendered once, on script load). */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    ...(isLoggingIn
                        ? { opacity: 0.4, pointerEvents: 'none' }
                        : {}),
                }}
            >
                <Box ref={googleButtonRef} sx={{ minHeight: 44 }} />

                <Button
                    variant="contained"
                    size="large"
                    startIcon={<AppleIcon />}
                    onClick={handleAppleLogin}
                    sx={{
                        width: 280,
                        bgcolor: '#000',
                        color: '#fff',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#222' },
                    }}
                >
                    Continuar con Apple
                </Button>
            </Box>
        </Paper>
    );
};

const mapStateToProps = (state) => {
    return {
        isLoggingIn: state.customer.isLoggingIn,
    };
};

export default connect(mapStateToProps, { loginWithGoogle, loginWithApple })(
    CustomerLogin
);
